import { useCallback, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { getAccessToken } from '@/api/client'

export function useAuth() {
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    fetchUser,
    clearError,
  } = useAuthStore()

  const isAdmin = user?.role === 'admin'

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    isAdmin,
    login,
    logout,
    fetchUser,
    clearError,
  }
}

export function useRequireAuth(redirectTo = '/login') {
  const { isAuthenticated, isLoading, fetchUser } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const token = getAccessToken()
    if (!token) {
      navigate(redirectTo, { state: { from: location }, replace: true })
      return
    }

    if (!isAuthenticated && !isLoading) {
      fetchUser().catch(() => {
        navigate(redirectTo, { state: { from: location }, replace: true })
      })
    }
  }, [isAuthenticated, isLoading, fetchUser, navigate, redirectTo, location])

  return { isAuthenticated, isLoading }
}

export function useRequireAdmin(redirectTo = '/') {
  const { isAuthenticated, isLoading, user } = useAuth()
  const navigate = useNavigate()

  useRequireAuth()

  useEffect(() => {
    if (!isLoading && isAuthenticated && user?.role !== 'admin') {
      navigate(redirectTo, { replace: true })
    }
  }, [isAuthenticated, isLoading, user, navigate, redirectTo])

  return { isAdmin: user?.role === 'admin', isLoading }
}

export function useLogout() {
  const { logout } = useAuth()
  const navigate = useNavigate()

  return useCallback(async () => {
    await logout()
    navigate('/login', { replace: true })
  }, [logout, navigate])
}
